/* eslint-disable @typescript-eslint/no-use-before-define */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { AnnotationTagOption } from '@euler/app/flows/media/annotation/functions/AnnotationTagOption';
import { DefaultAnnotationTag } from '@euler/app/flows/media/annotation/functions/DefaultAnnotationTag';
import { useImagePreviewSize } from '@euler/app/flows/media/annotation/functions/useImagePreviewSize';
import { useIsTabletLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { NavigationAction } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';
import {
  ImageAnnotationResult,
  ImageAnnotationView,
  ImageAnnotationViewRef,
} from './components/ImageAnnotationView';
import { TopActionBar } from './components/TopActionBar';
import { ImageAnnotationMetadata } from './functions/AnnotationMetadata';

export const ImageAnnotationScreen = wrapNavigatorScreen(
  ({
    imageUri,
    mask,
    defaultTag,
    tags,
    tagOptions,
    watermark,
    quality,
    annotationMetadata,
    onDone,
  }: {
    imageUri: string;
    mask?: { width: number; height: number };
    tags: string[];
    tagOptions?: AnnotationTagOption[];
    defaultTag?: DefaultAnnotationTag;
    quality?: number;
    watermark?: (scale?: number) => ReactNode;
    annotationMetadata?: ImageAnnotationMetadata;
    onDone?: (result: ImageAnnotationResult) => void;
  }) => {
    const { width, height } = useWindowDimensions();
    const navigation = useAppNavigation();
    const [isNavigatingAway, setIsNavigatingAway] = useState(false);
    const continueAction = useRef<NavigationAction>();
    const annotationViewRef = useRef<ImageAnnotationViewRef>(null);
    const isTablet = useIsTabletLayout();

    const [pw, ph] = useImagePreviewSize({
      mask,
      annotationMetadata,
      container: {
        width,
        height,
      },
    });

    const pixelScale = Math.min(pw, ph) / 375;

    useEffect(() => {
      return navigation.addListener('beforeRemove', e => {
        e.preventDefault();
        continueAction.current = e.data.action;
        annotationViewRef.current?.hideControls();
        setIsNavigatingAway(true);
      });
    }, [navigation]);

    const onRetake = useCallback(() => {
      if (Platform.OS !== 'web') {
        navigation.goBack();
      } else {
        navigation.pop(2);
      }
    }, [navigation]);

    const onConfirm = useCallback(async () => {
      if (annotationViewRef.current == null) return;

      const result = await annotationViewRef.current.getAnnotationResult();

      if (result != null) {
        onDone?.(result);
      }
    }, [onDone]);

    return (
      <View
        css={`
          flex: 1;
          background-color: black;
        `}
      >
        <ImageAnnotationView
          imageUri={imageUri}
          containerWidth={width}
          containerHeight={height}
          width={pw}
          height={ph}
          mask={mask}
          watermark={watermark}
          tags={tags}
          tagOptions={tagOptions}
          defaultTag={defaultTag}
          quality={quality}
          annotationMetadata={annotationMetadata}
          ref={annotationViewRef}
          tagBarBlurred={isTablet}
        />

        <TopActionBar
          onRetake={onRetake}
          onConfirm={onConfirm}
          scale={pixelScale}
          blur={isTablet}
        />
        <AnimatePresence
          onExitComplete={() => {
            if (continueAction.current) {
              navigation.dispatch(continueAction.current);
            }
          }}
        >
          {!isNavigatingAway && (
            <MotiView
              from={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'timing',
              }}
              css={`
                width: 0px;
                height: 0px;
              `}
            />
          )}
        </AnimatePresence>
      </View>
    );
  },
  {
    headerShown: false,
    cardStyle: {
      backgroundColor: '#000000',
    },
    animationEnabled: false,
  },
);
